// ── Trenching math (Ryan, Oct 23 2026) ─────────────────────────────────────────
// Shared by Settings → Trenching (what a setup costs a day) and a bid line's
// Trenching window (the whole estimate → sub-lines). Width and depth in inches.

export const n = v => (Number.isFinite(Number(v)) ? Number(v) : 0);
// Cubic yards of trench per linear foot.
export const cyPerLf = (widthIn, depthIn) => (n(widthIn) / 12) * (n(depthIn) / 12) / 27;
// A production setup's cost for one day: equipment ($/day × how many) + labor (people × hours × rate).
export function setupDay(setup) {
  const d = (setup && setup.data) || setup || {};
  const equip = (d.equipment || []).reduce((s, e) => s + n(e.qty || 1) * n(e.per_day), 0);
  const labor = (d.labor || []).reduce((s, l) => s + n(l.people || 1) * n(l.hours || 8) * n(l.rate), 0);
  const hours = (d.labor || []).reduce((s, l) => s + n(l.people || 1) * n(l.hours || 8), 0);
  return { equip, labor, total: equip + labor, hours };
}
export const money = v => '$' + n(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const r2 = v => Math.round(n(v) * 100) / 100;

// How much slower than a car a truck drives (Ryan, Oct 28 2026): the road routes are car times, and a loaded
// 10-wheeler or end dump is slower — 1.2 = 20% longer on every drive. Set per truck in Settings → Trenching;
// blank / 1 = car time.
export const truckFactor = truck => { const f = n(truck && truck.data && truck.data.speed_factor); return f > 0 ? f : 1; };

// One truck run: drive there and back (at the truck's own pace) + time in and out at the pit/dump + unloading.
// loads = how many trips; truck hours = loads × the round trip.
export function haul({ cy, truck, oneWayMin, siteMin, unloadMin }) {
  const cap = n(truck && truck.data && truck.data.capacity_cy) || 0;
  const loads = cap > 0 ? Math.ceil(n(cy) / cap) : 0;
  const roundMin = 2 * n(oneWayMin) * truckFactor(truck) + n(siteMin) + n(unloadMin);
  const hours = (loads * roundMin) / 60;
  const rate = n(truck && truck.data && truck.data.hourly_cost);
  return { loads, roundMin, hours: r2(hours), rate, cost: hours * rate };
}

// The whole estimate. inp = { lf, width, depth, ftPerDay, setup, fillIn: {pit, material, cy, truck, oneWayMin},
// spoilsOut: {dump, cy, truck, oneWayMin}, settings }. Returns the numbers and the sub-lines for the bid.
export function trenchEstimate(inp) {
  const s = inp.settings || {};
  const lf = n(inp.lf);
  const days = n(inp.ftPerDay) > 0 ? lf / n(inp.ftPerDay) : 0;
  const trenchCy = lf * cyPerLf(inp.width, inp.depth);
  const d = (inp.setup && inp.setup.data) || {};
  const lines = [];
  // Production: each machine for the days; each labor role for its hours.
  for (const e of d.equipment || []) {
    if (!e.name) continue;
    lines.push({ description: `${e.name}${n(e.qty) > 1 ? ' ×' + n(e.qty) : ''} — ${r2(days)} day${r2(days) === 1 ? '' : 's'}`, quantity: r2(days * n(e.qty || 1)), unit: 'DAY',
      material_cost: 0, labor_hours: 0, dje_standard: n(e.per_day), notes: 'Trenching production' });
  }
  for (const l of d.labor || []) {
    if (!l.role) continue;
    const hrs = days * n(l.people || 1) * n(l.hours || 8);
    lines.push({ description: `${l.role}${n(l.people) > 1 ? ' ×' + n(l.people) : ''} — ${r2(days)} day${r2(days) === 1 ? '' : 's'}`, quantity: r2(hrs), unit: 'HR',
      material_cost: 0, labor_hours: 1, labor_rate: n(l.rate) || null, dje_standard: 0, notes: 'Trenching production' });
  }
  // Material in from a pit.
  let fill = null;
  const fi = inp.fillIn;
  if (fi && fi.pit && fi.material && n(fi.cy) > 0) {
    const m = fi.material;
    const perTon = String(m.unit || 'TON').toUpperCase() === 'TON';
    const tons = n(fi.cy) * (n(m.tons_per_cy) || 1.4);
    const h = haul({ cy: fi.cy, truck: fi.truck, oneWayMin: fi.oneWayMin, siteMin: fi.pit.data.in_out_min, unloadMin: s.unload_min });
    fill = { cy: r2(fi.cy), tons: r2(tons), qty: perTon ? r2(tons) : r2(fi.cy), unit: perTon ? 'TON' : 'CY', price: n(m.price), materialCost: (perTon ? tons : n(fi.cy)) * n(m.price), ...h };
    lines.push({ description: `${m.name} — ${fi.pit.name}`, quantity: fill.qty, unit: fill.unit, material_cost: n(m.price), labor_hours: 0, dje_standard: 0, notes: `${r2(fi.cy)} CY` });
    lines.push({ description: `Trucking in — ${h.loads} load${h.loads === 1 ? '' : 's'} × ${Math.round(h.roundMin)} min (${fi.truck ? fi.truck.name : 'truck'})`, quantity: h.hours, unit: 'HR',
      material_cost: 0, labor_hours: 0, dje_standard: h.rate, notes: `${fi.pit.name}: ${Math.round(n(fi.oneWayMin))} min each way` });
  }
  // Spoils out to a dump.
  let spoils = null;
  const so = inp.spoilsOut;
  if (so && so.dump && n(so.cy) > 0) {
    const h = haul({ cy: so.cy, truck: so.truck, oneWayMin: so.oneWayMin, siteMin: so.dump.data.in_out_min, unloadMin: s.unload_min });   // loading at the job takes about what unloading does
    const fee = n(so.dump.data.dump_fee); const unit = String(so.dump.data.dump_fee_unit || 'LOAD').toUpperCase();
    const feeQty = unit === 'CY' ? n(so.cy) : unit === 'TON' ? n(so.cy) * 1.4 : h.loads;
    spoils = { cy: r2(so.cy), ...h, fee, feeUnit: unit, feeQty: r2(feeQty), feeCost: fee * feeQty };
    lines.push({ description: `Trucking spoils out — ${h.loads} load${h.loads === 1 ? '' : 's'} × ${Math.round(h.roundMin)} min (${so.truck ? so.truck.name : 'truck'})`, quantity: h.hours, unit: 'HR',
      material_cost: 0, labor_hours: 0, dje_standard: h.rate, notes: `${so.dump.name}: ${Math.round(n(so.oneWayMin))} min each way` });
    if (fee > 0) lines.push({ description: `Dump fee — ${so.dump.name}`, quantity: r2(feeQty), unit: unit === 'LOAD' ? 'EACH' : unit, material_cost: 0, labor_hours: 0, dje_standard: fee, notes: `${r2(so.cy)} CY of spoils` });
  }
  const day = setupDay(inp.setup);
  return { lf, days: r2(days), trenchCy: r2(trenchCy), day, production: day.total * days, fill, spoils, lines };
}
